// Token counting for the always-loaded skill index (01 "Skill index"; 02
// `agent_template_version.skill_index_tokens`, "measured at validation with the lead model's
// tokenizer"). The lead tokenizer lives behind the gateway, so it is injected. The built-in
// estimator is advisory only: it is reported, never used to make a version deployable.
import type { SkillIndexEntry } from "../schemas";

export type TokenCounter = {
  /** Recorded in the validation report so a count can be reproduced. */
  readonly name: string;
  /** Must return a finite, non-negative integer; anything else fails validation. */
  count(text: string): number;
};

/**
 * Advisory estimator, deterministic and dependency-free:
 * - each run of ASCII letters/digits counts ceil(length / 4);
 * - every other non-whitespace code point counts 1;
 * - a single whitespace character counts 0; a longer whitespace run counts ceil(length / 4).
 */
export const estimateTokens: TokenCounter = {
  name: "estimate-v2",
  count(text: string): number {
    let tokens = 0;
    for (const m of text.matchAll(/[A-Za-z0-9]+|\s+|\S/gu)) {
      const piece = m[0];
      if (/^\s+$/u.test(piece)) tokens += piece.length > 1 ? Math.ceil(piece.length / 4) : 0;
      else tokens += /^[A-Za-z0-9]+$/.test(piece) ? Math.ceil(piece.length / 4) : 1;
    }
    return tokens;
  },
};

/** Default cap (02 `skill_index_token_cap` default 2000). */
export const DEFAULT_SKILL_INDEX_TOKEN_CAP = 2000;

/**
 * Cheap upper-bound guard checked before any tokenizer runs: the rendered index may not exceed
 * `cap × MAX_BYTES_PER_TOKEN` UTF-8 bytes (16 000 bytes at the default cap), so pathological
 * input (a million spaces) is refused regardless of how a tokenizer merges it. Provisional.
 */
export const MAX_BYTES_PER_TOKEN = 8;

export function utf8ByteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/**
 * The text that is always loaded: one line per skill, in skill-key order. This is exactly what
 * is measured and what the bundle ships as `skill-index.md`.
 */
export function renderSkillIndex(entries: readonly SkillIndexEntry[]): string {
  return [...entries]
    .sort((a, b) => (a.skill < b.skill ? -1 : a.skill > b.skill ? 1 : 0))
    .map((e) => `- ${e.skill}: apply when ${e.applyWhen}\n`)
    .join("");
}
